import { buildLocalAnswer, findFaqAnswer, ILOG_KNOWLEDGE } from '../data/chatbotKnowledge';

const MODEL_CANDIDATES = [
  'gemini-2.5-flash',
  'gemini-2.5-flash-lite',
  'gemini-3.5-flash-lite',
];
const RATE_LIMIT_COOLDOWN_MS = 60_000;

/** @type {{ role: 'user' | 'model', parts: { text: string }[] }[]} */
let history = [];
let rateLimitedUntil = 0;

function getApiKey() {
  return (process.env.REACT_APP_GEMINI_API_KEY || '').trim();
}

export function getChatbotMode() {
  const geminiFlag = process.env.REACT_APP_CHATBOT_USE_GEMINI === 'true';
  const hasKey = Boolean(getApiKey());

  if (geminiFlag && hasKey) {
    return {
      id: 'gemini',
      label: 'Gemini 모드',
      detail: 'AI 답변 우선 · 실패 시 FAQ',
    };
  }

  if (geminiFlag && !hasKey) {
    return {
      id: 'faq',
      label: 'FAQ 모드',
      detail: 'Gemini 키가 없어 FAQ로 동작',
    };
  }

  return {
    id: 'faq',
    label: 'FAQ 모드',
    detail: '로컬 지식베이스로 답변',
  };
}

function isGeminiEnabled() {
  return process.env.REACT_APP_CHATBOT_USE_GEMINI === 'true' && Boolean(getApiKey());
}

function buildContents(question) {
  return [
    ...history,
    {
      role: 'user',
      parts: [{ text: question }],
    },
  ];
}

async function generateWithGemini(question, apiKey) {
  let lastError = null;

  for (const model of MODEL_CANDIDATES) {
    try {
      const url = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${encodeURIComponent(apiKey)}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          systemInstruction: {
            parts: [{ text: ILOG_KNOWLEDGE }],
          },
          contents: buildContents(question),
          generationConfig: {
            temperature: 0.55,
            maxOutputTokens: 512,
          },
        }),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message =
          payload?.error?.message || `Gemini API error (${response.status})`;
        const error = new Error(message);
        error.status = response.status;
        // 모델 만료/미지원이면 다음 모델 시도
        if (
          response.status === 404 ||
          /no longer available|not found|not supported/i.test(message)
        ) {
          lastError = error;
          continue;
        }
        throw error;
      }

      const text = payload?.candidates?.[0]?.content?.parts
        ?.map((part) => part.text || '')
        .join('')
        .trim();

      if (!text) {
        throw new Error('empty response');
      }

      return { text, model };
    } catch (error) {
      lastError = error;
      if (
        error.status === 404 ||
        /no longer available|not found|not supported/i.test(error.message || '')
      ) {
        continue;
      }
      throw error;
    }
  }

  throw lastError || new Error('사용 가능한 Gemini 모델이 없습니다.');
}

function toResult(text, source, note = '') {
  return { text, source, note };
}

export function resetChatbotSession() {
  history = [];
}

export async function askChatbot(question) {
  const trimmed = question.trim();
  if (!trimmed) {
    throw new Error('질문을 입력해 주세요.');
  }

  const localAnswer = buildLocalAnswer(trimmed);
  const faqMatched = Boolean(findFaqAnswer(trimmed));

  if (!isGeminiEnabled()) {
    return toResult(localAnswer, 'faq', '현재 FAQ 모드입니다.');
  }

  if (Date.now() < rateLimitedUntil) {
    return toResult(
      localAnswer,
      'faq',
      'Gemini 한도 초과로 FAQ 답변을 사용했습니다.'
    );
  }

  // FAQ로 정확히 매칭되면 API 호출 없이 FAQ 사용
  if (faqMatched) {
    return toResult(localAnswer, 'faq', 'FAQ 매칭 답변');
  }

  try {
    const { text: answer, model } = await generateWithGemini(trimmed, getApiKey());
    history = [
      ...history,
      { role: 'user', parts: [{ text: trimmed }] },
      { role: 'model', parts: [{ text: answer }] },
    ].slice(-8);
    return toResult(answer, 'gemini', `Gemini AI 답변 (${model})`);
  } catch (error) {
    if (error.status === 429) {
      rateLimitedUntil = Date.now() + RATE_LIMIT_COOLDOWN_MS;
      console.warn('[chatbot] Gemini quota exceeded. Using FAQ.');
      return toResult(
        localAnswer,
        'faq',
        'Gemini 429(한도 초과) → FAQ로 대체'
      );
    }

    console.error('[chatbot]', error);
    return toResult(
      localAnswer,
      'faq',
      `Gemini 오류 → FAQ로 대체 (${error.message || 'unknown'})`
    );
  }
}
